import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, local } from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_SETTINGS } from "@/data/reminders";
import { CheckinReminderCard } from "@/components/CheckinReminderCard";

const ROWS = [
  ["moodCheckins", "📋 Mood check-ins", "times"], ["meditation", "🧘 Meditation", "time"], ["habits", "✅ Habit tracking", "time"],
  ["safetyPlan", "🛡️ Review safety plan", "frequency"], ["emergencyContact", "📞 Reach out to a contact", "frequency"],
];

export default function Reminders() {
  const [s, setS] = useState({ ...DEFAULT_SETTINGS, ...local.get("reminder_settings", {}) });
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const save = (n) => {
    setS(n);
    local.set("reminder_settings", n);
    api.put("/settings", { notifications_enabled: Object.values(n).some((v) => v?.enabled), morning_reminder: n.moodCheckins.times[0], evening_reminder: n.moodCheckins.times.at(-1) }).catch(() => {});
  };
  const set = (k, patch) => save({ ...s, [k]: { ...s[k], ...patch } });
  const askPerm = async () => { const p = await Notification.requestPermission(); setPerm(p); if (p === "granted") new Notification("Mindful Me", { body: "Reminders are on 💜" }); };

  useEffect(() => {
    if (perm !== "granted") return;
    const id = setInterval(() => {
      const hm = new Date().toTimeString().slice(0, 5);
      if (s.moodCheckins.enabled && s.moodCheckins.times.includes(hm)) new Notification("Time to check in 💜", { body: "How are you feeling right now?" });
      if (s.meditation.enabled && s.meditation.time === hm) new Notification("Mindful minute 🧘", { body: "Take a moment for a short meditation." });
      if (s.habits.enabled && s.habits.time === hm) new Notification("Habit check ✅", { body: "Log today's habits." });
    }, 60000);
    return () => clearInterval(id);
  }, [perm, s]);

  return (
    <div className="max-w-2xl" data-testid="reminders-page">
      <PageHeader back title="Reminders" subtitle="Gentle nudges to look after yourself" />
      <CheckinReminderCard />
      <h3 className="font-semibold text-sm text-mm-muted mb-3" data-testid="reminders-in-app-title">While Mindful Me is open</h3>
      {perm !== "granted" && perm !== "unsupported" && (
        <div className="mm-card p-5 mb-4 flex justify-between items-center gap-3"><span className="text-sm">Allow browser notifications to receive reminders.</span><button className="mm-btn" onClick={askPerm} data-testid="reminders-enable-notifications">Enable</button></div>
      )}
      <div className="space-y-3">
        {ROWS.map(([k, label, kind]) => (
          <div key={k} className="mm-card p-5" data-testid={`reminder-${k}`}>
            <div className="flex justify-between items-center"><span className="font-semibold">{label}</span><Switch checked={s[k].enabled} onCheckedChange={(v) => set(k, { enabled: v })} data-testid={`reminder-toggle-${k}`} /></div>
            {s[k].enabled && kind === "times" && (
              <div className="flex flex-wrap gap-2 mt-3">{s[k].times.map((t, i) => <input key={`${k}-slot-${i}`} type="time" className="mm-input !w-32 !py-2" value={t} onChange={(e) => set(k, { times: s[k].times.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`reminder-${k}-time-${i}`} />)}</div>
            )}
            {s[k].enabled && kind === "time" && <input type="time" className="mm-input !w-32 !py-2 mt-3" value={s[k].time} onChange={(e) => set(k, { time: e.target.value })} data-testid={`reminder-${k}-time`} />}
            {s[k].enabled && kind === "frequency" && (
              <div className="flex gap-2 mt-3">{["daily", "weekly", "monthly"].map((f) => <button key={f} className={`mm-chip ${s[k].frequency === f ? "mm-chip-on" : ""}`} onClick={() => set(k, { frequency: f })} data-testid={`reminder-${k}-${f}`}>{f}</button>)}</div>
            )}
          </div>
        ))}
        <div className="mm-card p-5">
          <div className="flex justify-between items-center"><span className="font-semibold">🌙 Quiet hours</span><Switch checked={s.quietHours.enabled} onCheckedChange={(v) => set("quietHours", { enabled: v })} data-testid="reminder-toggle-quiet" /></div>
          {s.quietHours.enabled && <div className="flex items-center gap-2 mt-3"><input type="time" className="mm-input !w-32 !py-2" value={s.quietHours.start} onChange={(e) => set("quietHours", { start: e.target.value })} />to<input type="time" className="mm-input !w-32 !py-2" value={s.quietHours.end} onChange={(e) => set("quietHours", { end: e.target.value })} /></div>}
        </div>
      </div>
      <button className="mm-btn-ghost mt-6" onClick={() => toast.success("Reminder settings saved")} data-testid="reminders-save">Save</button>
    </div>
  );
}
